import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { ExternalPoint, RequestMethodEnum, RequestOptions } from '@model';
import { AtlasPagesService, Type } from 'atlas-pages-loader';
import { HttpClient } from '@angular/common/http';
import { CookieService } from './cookie.service';
import * as convertKeys from 'convert-keys';
import { ExternalPointPost } from '../model/external-point.model';

@Injectable()
export class ExternalPointsService {
  public readonly externalPoints$ = new BehaviorSubject<any>(null);
  loadingExternalPoints$ = new BehaviorSubject<boolean>(true);
  pagesLoaderService: AtlasPagesService;

  constructor(private apiService: ApiService, private httpClient: HttpClient, cookieService: CookieService) {
    this.pagesLoaderService = new AtlasPagesService(
      cookieService.getMarketsMock(),
      cookieService.getEnocSession(),
      this.httpClient,
    );
  }

  public async createExternalPoint(externalPoint: ExternalPointPost): Promise<ExternalPointPost> {
    try {
      const createdExternalPoint = await this.apiService.post(`external-points`, externalPoint);
      return createdExternalPoint;
    } catch (err) {
      console.log('Could not create external point.', err);
      throw err;
    }
  }

  public async updateExternalPoint(id: string, externalPoint: ExternalPointPost): Promise<ExternalPointPost> {
    try {
      const updatedExternalPoint = await this.apiService.put(`external-points/${id}`, externalPoint);
      return updatedExternalPoint;
    } catch (err) {
      console.log('Could not update external point.', err);
      throw err;
    }
  }

  public async getExternalPoints(id: string, id_type: string, sdp_id?: string) {
    try {
      this.loadingExternalPoints$.next(true);
      const typesResponse = await this.pagesLoaderService.getAllPagesForType('externalpoint', {
        id: id,
        dr_type: id_type,
      });
      const siteExternalPoints = convertKeys.toCamel(typesResponse.types) as Type[];
      const sdpExternalPoints = siteExternalPoints.filter((externalPoint) => externalPoint.parentId === sdp_id);
      const externalPointIds = sdpExternalPoints.map((type) => type.id) || [];
      const externalPoints = await this.getExternalPointsByIds(externalPointIds);
      this.externalPoints$.next(externalPoints);
      this.loadingExternalPoints$.next(false);
    } catch (err) {
      this.externalPoints$.next([]);
      this.loadingExternalPoints$.next(false);
      console.log(`Could not load External Points.`, err);
    }
  }

  private async getExternalPointsByIds(ids: string[]): Promise<ExternalPoint[]> {
    try {
      const response = await this.apiService.post('bulk-points', { ids: ids });

      if (!Array.isArray(response)) {
        throw new Error('Something went wrong while retrieving external points by ids.');
      }
      const externalPoints = convertKeys.toCamel(response) as ExternalPoint[];
      const result: ExternalPoint[] = externalPoints.map((point, i) => ({
        ...point,
        displayLabels: response[i]?.display_labels ?? point.displayLabels,
      }));
      result.sort((a, b) => Date.parse(b.createDttm || '') - Date.parse(a.createDttm || ''));
      return result;
    } catch (err) {
      console.log('Could not retrieve external points by ids.', err);
      throw err;
    }
  }
}
